// Role-editor permission dependency engine. FE mirror of the BE delegation.js
// rule families — keep the edge data aligned.
//
// TAXONOMY (Base Permissions, restructure 2026-08):
//   Administration            User/Role/Device/Group Management, Solution Management (master), Brand Management
//   Solution Management       Solution Creation, Solution Edit, Threshold Management
//   Solution Features         the 14 feature masters (first layer over per-solution assets)
//   Solution Edit   the 7 editing parts (role level: they apply to every solution the role holds)
//   Analytics                 Self-Service, Trend Forecasting, Advanced, Comparative
//
// Operates on the two role-editor state atoms:
//   utils               : [{ name, tabs:[{ name, permission }], overall }]   (Base Permissions matrix)
//   servicePermissions  : [{ serviceId, name, assets:[{ name, permissions }], overall }]  (Per Solution matrix)
// permission values are "ALL" | "READ" | "disable" (editor-only; "disable" == not granted).
//
// Rule families (BE-enforced mirrors):
//   R1  Solution Management row tabs <= Administration > Solution Management (cap)
//   R2  Solution Creation => Solution Edit page + all 7 Parts, mirror-rank (raise + cascade)
//   R3  Threshold Management => Features Rules/Alarms/Controlling, mirror-rank (raise + cascade)
//   R5  Features master >= per-solution asset (14 gates; grey-until-held + cascade)
//   R7  Settings Parts <= Solution Management > Solution Edit page (cap)
//   R9  per-solution Group Management <= Administration > Group Management
//   R10 per-solution Scheduled Reports / Threshold Profiles <= their Features masters
//   SR-edge  Features > Scheduled Reports requires Features > Alarms (Partial)

const RANK = { READ: 1, ALL: 2 };
export const rank = (p) => RANK[p] || 0; // "disable"/undefined -> 0
const permOf = (r) => (r >= 2 ? "ALL" : r >= 1 ? "READ" : "disable");
// UI vocabulary: never surface the raw ALL/READ/disable values — the UI convention is
// Full Access / Partial Access / Disabled everywhere.
export const permLabel = (p) =>
  p === "ALL" ? "Full Access" : p === "READ" ? "Partial Access" : "Disabled";

// ── Taxonomy normalizer (load-time, old -> new names) ───────────────────────
// Role docs may still carry the legacy taxonomy until the data migrations run.
// EVERY ingestion point (metaDataSlice.setRole, the RTK roles service, the
// editor's target role) normalizes through these, so the engine, editor, and
// all consumers only ever see the target names. Mirrors the BE canonicalizer.
// Full legacy chain -> canonical "Solution Edit" (Solution Editing -> Solution
// Settings Parts -> Solution Edit).
const MODULE_RENAMES = {
  "Solution Editing": "Solution Edit",
  "Solution Settings Parts": "Solution Edit",
};
const MODULE_TAB_MOVES = {
  "Solution Management": {
    "Solution Creator": { tab: "Solution Creation" },
    "Thresholds Management": { tab: "Threshold Management" },
    // Solution Management > "Solution Settings" page tab renamed to "Solution Edit".
    "Solution Settings": { tab: "Solution Edit" },
  },
  Analytics: {
    "Self-Service Analytics": { tab: "Self-Service" },
    "Advanced Analytics": { tab: "Advanced" },
    "Comparative Analytics": { tab: "Comparative" },
    "Predictive Analytics": { tab: "Trend Forecasting" },
  },
  Settings: {
    Branding: { module: "Administration", tab: "Brand Management" },
  },
  "Alarms Management": {
    "Alarms Management": { module: "Solution Features", tab: "Alarms" },
    "Scheduled Reports": { module: "Solution Features", tab: "Scheduled Reports" },
  },
};
const ASSET_RENAMES = {
  "Rule Management": "Rules",
  Configurations: "Configuration",
  Configration: "Configuration",
};
export const normalizeAssetName = (name) => ASSET_RENAMES[name] || name;

// modules[] -> normalized modules[] (renames + re-homes, collisions max-merged,
// rank-0 tabs dropped). Pure; input shape { name, tabs:[{name, permission}] }.
export function normalizeModules(modules) {
  const acc = {};
  const order = [];
  const ensure = (name) => {
    if (!acc[name]) {
      acc[name] = {};
      order.push(name);
    }
    return acc[name];
  };
  for (const m of modules || []) {
    if (!m || !m.name) continue;
    const granted = (m.tabs || []).filter((t) => t && t.name && rank(t.permission) >= 1);
    if (granted.length === 0) {
      ensure(MODULE_RENAMES[m.name] || m.name);
      continue;
    }
    for (const t of granted) {
      const move = (MODULE_TAB_MOVES[m.name] || {})[t.name];
      const modName = move && move.module ? move.module : MODULE_RENAMES[m.name] || m.name;
      const tabName = move ? move.tab : t.name;
      const bucket = ensure(modName);
      bucket[tabName] = Math.max(bucket[tabName] || 0, rank(t.permission));
    }
  }
  return order.map((name) => ({
    name,
    tabs: Object.entries(acc[name]).map(([tab, r]) => ({ name: tab, permission: permOf(r) })),
  }));
}

// services[] -> normalized services[] (asset renames, duplicate serviceId rows
// max-merged, rank-0 tabs dropped). Non-tab row fields pass through.
export function normalizeServices(services) {
  const acc = {};
  const order = [];
  for (const s of services || []) {
    if (!s || s.serviceId == null) continue;
    const sid = String(s.serviceId);
    if (!acc[sid]) {
      acc[sid] = { row: s, tabs: {} };
      order.push(sid);
    }
    for (const t of s.tabs || []) {
      if (!t || !t.name) continue;
      const r = rank(t.permission);
      if (r < 1) continue;
      const name = normalizeAssetName(t.name);
      acc[sid].tabs[name] = Math.max(acc[sid].tabs[name] || 0, r);
    }
  }
  return order.map((sid) => ({
    ...acc[sid].row,
    tabs: Object.entries(acc[sid].tabs).map(([name, r]) => ({ name, permission: permOf(r) })),
  }));
}

// Whole role doc (modules + services normalized; everything else passes through).
export function normalizeRoleDoc(role) {
  if (!role || typeof role !== "object") return role;
  return {
    ...role,
    modules: normalizeModules(role.modules),
    services: normalizeServices(role.services),
  };
}

// ── Structural constants ────────────────────────────────────────────────────
export const SETTINGS_PARTS = [
  "Key Info",
  "Asset",
  "Monitoring",
  "Value Insights",
  "Controlling",
  "Configurations",
  "Layout",
];
export const SM_ROW_TABS = ["Solution Creation", "Solution Edit", "Threshold Management"];
export const FEATURES = [
  "Digital Twin",
  "Monitoring",
  "Controlling",
  "Configuration",
  "Events",
  "Tracking",
  "Rules",
  "Alarms",
  "Scheduled Reports",
  "Threshold Profiles",
  "Analytics",
  "Metadata",
  "Video Analytics",
  "History",
];

export const DEPENDENCY_EDGES = [
  {
    id: "threshold-management-requires-controlling",
    enforced: true,
    sourceModule: "Solution Management",
    sourceTab: "Threshold Management",
    depAsset: "Controlling",
    depLevel: "READ", // presence (READ suffices; never grants ALL device-actuation)
  },
  {
    id: "threshold-management-requires-rules",
    // LOCKED in the editor while Threshold Management is held (on solutions where the
    // conditional Rules tab exists) — auto-assigned at min(source, caller) and its
    // below-required levels grey. enforced stays false so the BE never rejects (the tab
    // does not exist on non-maintenance solutions).
    enforced: false,
    sourceModule: "Solution Management",
    sourceTab: "Threshold Management",
    depAsset: "Rules",
    depLevel: "same",
  },
];

// Scoping columns: they scope a capability over the row's features and are
// excluded from bulk gestures, row Overall summaries, and rowHeld semantics
// - but they DO render as matrix columns.
export const SCOPING_ASSETS = ["Scheduled Reports", "Threshold Profiles"];
export const isScopingAsset = (name) => SCOPING_ASSETS.indexOf(name) !== -1;

// First-layer rule: the Base Permissions matrix is the FIRST layer. A
// per-solution asset can only be granted at rank r if the role also holds its
// gate at rank >= r. The 14 features gate against their Solution Features
// masters (R5; Scheduled Reports / Threshold Profiles are the R10 instances),
// and Group Management rides the Administration master (R9).
export const ASSET_UTILITY_EDGES = [
  ...FEATURES.map((f) => ({ asset: f, module: "Solution Features", tab: f })),
  { asset: "Group Management", module: "Administration", tab: "Group Management" },
];

// Module tab RAISE edges (auto-select, top-down): granting/raising the source
// pulls the dep up; lowering the dep below the source cascades the source down
// (mirror semantics when depLevel === "same").
export const MODULE_MODULE_EDGES = [
  // R2: atomic creation — Solution Creation mirrors onto the Settings page and
  // every Settings Part (creating a solution configures all of them).
  {
    id: "creation-mirrors-settings-page",
    sourceModule: "Solution Management",
    sourceTab: "Solution Creation",
    sourceLabel: "Solution Creation",
    depModule: "Solution Management",
    depTab: "Solution Edit",
    depLevel: "same",
  },
  ...SETTINGS_PARTS.map((p) => ({
    id: `creation-mirrors-part-${p.toLowerCase().replace(/ /g, "-")}`,
    sourceModule: "Solution Management",
    sourceTab: "Solution Creation",
    sourceLabel: "Solution Creation",
    depModule: "Solution Edit",
    depTab: p,
    depLevel: "same",
  })),
  // R3: Threshold Management needs the rule/alarm/control features it drives.
  ...["Rules", "Alarms", "Controlling"].map((f) => ({
    id: `tm-requires-feature-${f.toLowerCase()}`,
    sourceModule: "Solution Management",
    sourceTab: "Threshold Management",
    sourceLabel: "Threshold Management",
    depModule: "Solution Features",
    depTab: f,
    depLevel: "same",
  })),
  // SR-edge: reports are built from alarm data.
  {
    id: "scheduled-reports-requires-alarms",
    sourceModule: "Solution Features",
    sourceTab: "Scheduled Reports",
    sourceLabel: "Scheduled Reports",
    depModule: "Solution Features",
    depTab: "Alarms",
    depLevel: "READ",
  },
];

// Module tab CAP edges (grey-until-held, mirror-down on gate lowering):
//   R1: the 3 Solution Management row tabs sit under the Administration master;
//   R7: the 7 Settings Parts sit under the Solution Edit page tab
//       (replaces the old door edges — Partial page now allows Partial parts).
export const MODULE_GATE_EDGES = [
  ...SM_ROW_TABS.map((tab) => ({
    tabModule: "Solution Management",
    tab,
    gateModule: "Administration",
    gateTab: "Solution Management",
  })),
  ...SETTINGS_PARTS.map((part) => ({
    tabModule: "Solution Edit",
    tab: part,
    gateModule: "Solution Management",
    gateTab: "Solution Edit",
  })),
];

export const ASSET_MODULE_EDGES = [];

// Within-Base "surface" edges (item 1): the base Analytics module's tabs are pure VIEW
// surfaces of the Solution Features > Analytics feature. This is a BIDIRECTIONAL cap between
// two BASE modules — DISTINCT from the base<->per-solution ceiling (auto-drop only) and from
// the R2/R3 raise family. Semantics: surface <= feature. Granting a base Analytics surface
// auto-RAISES the feature to >= the surface rank (closure PASS 1e); lowering/dropping the
// feature auto-CAPS/drops the surfaces (cascadeGates step 3b) and greys them above the
// feature rank (moduleTabSurfaceGateRank). Enforced by the BE R11 block (surface <= feature).
export const MODULE_SURFACE_EDGES = [
  "Self-Service",
  "Trend Forecasting",
  "Advanced",
  "Comparative",
].map((tab) => ({
  id: `analytics-surface-${tab.toLowerCase().replace(/[^a-z]+/g, "-")}`,
  surfaceModule: "Analytics",
  surfaceTab: tab,
  featureModule: "Solution Features",
  featureTab: "Analytics",
}));

// Same-row asset -> asset (dep column must exist; conditional tabs stay tolerant):
// Digital Twin's interactive hotspots are dead without Controlling on that solution.
// Threshold Profiles scopes profile view/apply on the row — applying a profile
// recreates that solution's rules/automations, so the row needs Controlling
// (presence) and Rules (mirror). These row-local edges carry the user-facing
// attribution ("Required by Threshold Profiles") on rows where the TP column is
// granted; the module-level Threshold Management edge remains the integrity
// backstop for rows without a TP column.
export const ASSET_ASSET_EDGES = [
  {
    id: "digital-twin-requires-controlling",
    sourceAsset: "Digital Twin",
    depAsset: "Controlling",
    depLevel: "READ",
  },
  {
    id: "threshold-profiles-require-controlling",
    sourceAsset: "Threshold Profiles",
    depAsset: "Controlling",
    depLevel: "READ",
  },
  {
    id: "threshold-profiles-require-rules",
    sourceAsset: "Threshold Profiles",
    depAsset: "Rules",
    depLevel: "same",
  },
  // Rules that drive an actuator need the Controlling surface — the rule
  // editor's "Actuations" authoring tab renders off Controlling. Presence
  // (READ) only, exactly like Threshold Profiles → Controlling: it never hands
  // actuation (every actuation affordance stays gated at Full), it just lets
  // the actuation tab render, so a Rules grant advises Controlling READ.
  {
    id: "rules-require-controlling",
    sourceAsset: "Rules",
    depAsset: "Controlling",
    depLevel: "READ",
  },
  // The Digital Twin SVG upload/reset buttons write onto the asset's metadata,
  // so authoring a Digital Twin advises Metadata. Presence (READ) only — the
  // upload button additionally needs Metadata at Full Access, which the admin
  // grants deliberately; this edge just surfaces the coupling, it never forces
  // the editing capability.
  {
    id: "digital-twin-requires-metadata",
    sourceAsset: "Digital Twin",
    depAsset: "Metadata",
    depLevel: "READ",
  },
];

const moduleTabRank = (utils, moduleName, tabName) => {
  const m = (utils || []).find((u) => u && u.name === moduleName);
  if (!m) return 0;
  const t = (m.tabs || []).find((x) => x && x.name === tabName);
  return t ? rank(t.permission) : 0;
};
// Item 1: the feature rank that caps a base "surface" tab (an Analytics tab under Solution
// Features > Analytics), or Infinity when the tab is not a surface. Mirror of
// moduleTabGateRank for the surface family; folded into the surface tab's greying via Math.min.
export const moduleTabSurfaceGateRank = (utils, moduleName, tabName) => {
  const edge = MODULE_SURFACE_EDGES.find(
    (e) => e.surfaceModule === moduleName && e.surfaceTab === tabName
  );
  if (!edge) return Infinity;
  return moduleTabRank(utils, edge.featureModule, edge.featureTab);
};
export const moduleTabSurfaceGateInfo = (moduleName, tabName) => {
  const edge = MODULE_SURFACE_EDGES.find(
    (e) => e.surfaceModule === moduleName && e.surfaceTab === tabName
  );
  return edge ? { gateModule: edge.featureModule, gateTab: edge.featureTab } : null;
};
// OPERATIONALLY held: scoping entries do not make a row "held" for dependency
// purposes (a Thresholds grant must never auto-select Controlling onto a
// scoping-only row), and the row Overall stays an operational summary.
const rowHeld = (row) =>
  (row.assets || []).some(
    (a) => a && !isScopingAsset(a.name) && rank(a.permissions) >= 1
  );
// The BE enforces required deps (Threshold Management -> Controlling) on ANY row carrying
// ANY granted tab (scoping included - BE dependencyViolations uses hasAnyTab), so the
// ENFORCED-edge net must match it, or a scoping-only row passes the editor but 400s on
// save (audit finding 2). Advisory edges keep the narrower operational rowHeld above (they
// are BE-legal and never 400).
const rowHasAnyTab = (row) =>
  (row.assets || []).some((a) => a && rank(a.permissions) >= 1);
const recomputeRowOverall = (row) => {
  const perms = (row.assets || [])
    .filter((a) => a && !isScopingAsset(a.name))
    .map((a) => a.permissions);
  if (perms.every((p) => p === "ALL")) row.overall = "ALL";
  else if (perms.every((p) => p === "READ")) row.overall = "READ";
  else if (perms.every((p) => !p || p === "disable")) row.overall = "disable";
  else row.overall = "";
};
const recomputeModuleOverall = (mod) => {
  const named = (mod.tabs || []).filter((t) => t && t.name);
  if (named.length && named.every((t) => t.permission === "ALL")) mod.overall = "ALL";
  else if (named.length && named.every((t) => t.permission === "READ")) mod.overall = "READ";
  else if (named.every((t) => !t.permission || t.permission === "disable")) mod.overall = "disable";
  else mod.overall = "";
};

// The required dep level for `asset` given the current utils, or 0 if no enforced edge's
// source is held (i.e. the dep is currently free to lower).
export function requiredEnforcedDepLevel(utils, asset) {
  let need = 0;
  for (const e of DEPENDENCY_EDGES) {
    if (!e.enforced || e.depAsset !== asset) continue;
    if (moduleTabRank(utils, e.sourceModule, e.sourceTab) < 1) continue;
    const lvl = e.depLevel === "same"
      ? moduleTabRank(utils, e.sourceModule, e.sourceTab)
      : rank(e.depLevel);
    need = Math.max(need, lvl);
  }
  return need;
}
export const isEnforcedDepAsset = (asset) =>
  DEPENDENCY_EDGES.some((e) => e.enforced && e.depAsset === asset);

// UX lock level for a dep asset on a solution row while its source is held: the highest level
// ANY held edge requires (enforced edges at their fixed/"same" level; advisory edges capped to the
// caller's own rank for that cell so a delegated admin is never locked above what they can grant).
// In the cell UI, level controls strictly BELOW this are greyed with a "required by ..." tooltip;
// the lock lifts automatically once the source tab is removed (this returns 0). callerRank may be
// Infinity for a ⊤ caller (no cap). Returns { level, by } — by names the requirer.
export function lockedDepLevelInfo(utils, asset, callerRank) {
  let need = 0;
  let by = "";
  for (const e of DEPENDENCY_EDGES) {
    if (e.depAsset !== asset) continue;
    const src = moduleTabRank(utils, e.sourceModule, e.sourceTab);
    if (src < 1) continue; // source not held -> this edge imposes nothing
    let lvl = e.depLevel === "same" ? src : rank(e.depLevel);
    if (!e.enforced && typeof callerRank === "number" && isFinite(callerRank)) {
      lvl = Math.min(lvl, callerRank); // advisory: never lock above what the caller holds
    }
    if (lvl > need) {
      need = lvl;
      by = e.sourceTab;
    }
  }
  return { level: need, by };
}
export function lockedDepLevel(utils, asset, callerRank) {
  return lockedDepLevelInfo(utils, asset, callerRank).level;
}

// Auto-assign closure. Deep-clones both atoms, raises deps to satisfy each edge whose
// source is held, and returns the (possibly-changed) atoms + the raises.
// `callerRankFor(serviceId, asset)` (optional) caps advisory edges to the caller's own rank.
// CHANGE-TRIGGERED closure: auto-select edges fire ONLY when the current gesture RAISES
// their source above the `baseline` (the state before the gesture). A decreasing or
// unrelated gesture must never raise anything — latent gaps in legal legacy states are
// left alone. Omitting `baseline` means "everything is newly granted" (create/clone
// seeds), which fires fully.
export function computeDependencyClosure({ utils, servicePermissions, callerRankFor, baseline }) {
  const u = JSON.parse(JSON.stringify(utils || []));
  const sp = JSON.parse(JSON.stringify(servicePermissions || []));
  const baseModuleRank = (moduleName, tabName) =>
    baseline ? moduleTabRank(baseline.utils || [], moduleName, tabName) : 0;
  const baseAssetRank = (serviceId, assetName) => {
    if (!baseline) return 0;
    const row = (baseline.servicePermissions || []).find(
      (x) => x && String(x.serviceId) === String(serviceId)
    );
    const a = row && (row.assets || []).find((x) => x && x.name === assetName);
    return a ? rank(a.permissions) : 0;
  };
  const autoAssigned = [];
  for (const edge of DEPENDENCY_EDGES) {
    const srcRank = moduleTabRank(u, edge.sourceModule, edge.sourceTab);
    if (srcRank < 1) continue; // source not held
    if (srcRank <= baseModuleRank(edge.sourceModule, edge.sourceTab)) continue; // not raised by this gesture
    let targetRank = edge.depLevel === "same" ? srcRank : rank(edge.depLevel);
    for (const row of sp) {
      // enforced edges must match the BE's hasAnyTab net (edit/scoping rows included);
      // advisory edges stay on the narrower operational rowHeld.
      if (!(edge.enforced ? rowHasAnyTab(row) : rowHeld(row))) continue;
      const a = (row.assets || []).find((x) => x && x.name === edge.depAsset);
      if (!a) continue; // optional column absent on this solution -> no edge
      let want = targetRank;
      if (!edge.enforced && typeof callerRankFor === "function") {
        // advisory: never exceed what the caller holds for this cell
        want = Math.min(want, callerRankFor(row.serviceId, edge.depAsset));
      }
      if (want >= 1 && rank(a.permissions) < want) {
        const from = a.permissions;
        a.permissions = permOf(want);
        recomputeRowOverall(row);
        autoAssigned.push({
          target: "serviceAsset",
          serviceId: row.serviceId,
          serviceName: row.name,
          asset: edge.depAsset,
          from,
          to: a.permissions,
          edgeId: edge.id,
          enforced: edge.enforced,
        });
      }
    }
  }
  // PASS 1b: same-row asset -> asset (e.g. Digital Twin pulls Controlling READ where the
  // Controlling column exists on that solution). Runs before PASS 2 so any raise here can
  // still pull its utility gate through the chain.
  for (const edge of ASSET_ASSET_EDGES) {
    for (const row of sp) {
      const src = (row.assets || []).find((x) => x && x.name === edge.sourceAsset);
      if (!src || rank(src.permissions) < 1) continue;
      if (rank(src.permissions) <= baseAssetRank(row.serviceId, edge.sourceAsset))
        continue; // not raised by this gesture
      const dep = (row.assets || []).find((x) => x && x.name === edge.depAsset);
      if (!dep) continue; // conditional column absent on this solution
      let want =
        edge.depLevel === "same" ? rank(src.permissions) : rank(edge.depLevel);
      if (typeof callerRankFor === "function") {
        // advisory: never raise a cell above what the caller holds for it (mirrors PASS 1).
        // Without this a delegated admin gets an invisible over-grant that the greyed _capAll
        // control forbids and the BE servicesSubset 403s on save (audit finding 1).
        want = Math.min(want, callerRankFor(row.serviceId, edge.depAsset));
      }
      if (want >= 1 && rank(dep.permissions) < want) {
        const from = dep.permissions;
        dep.permissions = permOf(want);
        recomputeRowOverall(row);
        autoAssigned.push({
          target: "serviceAsset",
          serviceId: row.serviceId,
          serviceName: row.name,
          asset: edge.depAsset,
          from,
          to: dep.permissions,
          edgeId: edge.id,
          enforced: false,
        });
      }
    }
  }
  // Shared: raise a module tab to `want`, recording the change.
  const raiseModuleTab = (moduleName, tabName, want, edgeId) => {
    const m = u.find((x) => x && x.name === moduleName);
    if (!m) return;
    const t = (m.tabs || []).find((x) => x && x.name === tabName);
    if (!t) return;
    if (rank(t.permission) < want) {
      const from = t.permission;
      t.permission = permOf(want);
      recomputeModuleOverall(m);
      autoAssigned.push({
        target: "utilityTab",
        module: moduleName,
        tab: tabName,
        from,
        to: t.permission,
        edgeId,
        enforced: true,
      });
    }
  };
  // PASS 1d: module tab -> module tab RAISE edges (R2 creation mirror, R3
  // Threshold Management features, SR-edge), iterated to a fixpoint so chained
  // raises settle. An edge fires when its source sits above the baseline
  // (gesture-raised) OR was raised by an earlier edge in this same closure.
  const raisedByClosure = new Set();
  for (let guard = 0; guard < MODULE_MODULE_EDGES.length + 2; guard++) {
    const before = autoAssigned.length;
    for (const edge of MODULE_MODULE_EDGES) {
      const srcNow = moduleTabRank(u, edge.sourceModule, edge.sourceTab);
      if (srcNow < 1) continue;
      const key = `${edge.sourceModule}|${edge.sourceTab}`;
      const gestureRaised = srcNow > baseModuleRank(edge.sourceModule, edge.sourceTab);
      if (!gestureRaised && !raisedByClosure.has(key)) continue;
      const want = edge.depLevel === "same" ? srcNow : rank(edge.depLevel);
      const depBefore = moduleTabRank(u, edge.depModule, edge.depTab);
      raiseModuleTab(edge.depModule, edge.depTab, want, edge.id);
      if (moduleTabRank(u, edge.depModule, edge.depTab) > depBefore) {
        raisedByClosure.add(`${edge.depModule}|${edge.depTab}`);
      }
    }
    if (autoAssigned.length === before) break;
  }
  // PASS 1e: base "surface" tab -> Solution Features feature RAISE (item 1). Granting a base
  // Analytics surface requires (and auto-raises) the Analytics feature to >= the surface rank.
  // Change-triggered off the baseline like the other raise passes; enforced (BE R11 parity).
  for (const edge of MODULE_SURFACE_EDGES) {
    const srcRank = moduleTabRank(u, edge.surfaceModule, edge.surfaceTab);
    if (srcRank < 1) continue; // surface not held
    if (srcRank <= baseModuleRank(edge.surfaceModule, edge.surfaceTab)) continue; // not raised this gesture
    raiseModuleTab(edge.featureModule, edge.featureTab, srcRank, edge.id);
  }
  // PASS 2 (top-down model): raise a utility gate ONLY for cells PASS 1 itself raised
  // (the dependency chain, e.g. Threshold Management -> Controlling READ pulls the
  // Features > Controlling master). A MANUAL/bulk cell pushed above its gate must NOT
  // raise the gate — cascadeGates() mirrors it back down instead (base layer first).
  for (const aa of autoAssigned) {
    if (aa.target !== "serviceAsset") continue;
    const edge = ASSET_UTILITY_EDGES.find((e) => e.asset === aa.asset);
    if (!edge) continue;
    const need = rank(aa.to);
    const m = u.find((x) => x && x.name === edge.module);
    if (!m) continue;
    const t = (m.tabs || []).find((x) => x && x.name === edge.tab);
    if (!t) continue;
    if (rank(t.permission) < need) {
      const from = t.permission;
      t.permission = permOf(need);
      recomputeModuleOverall(m);
      autoAssigned.push({
        target: "utilityTab",
        module: edge.module,
        tab: edge.tab,
        from,
        to: t.permission,
        edgeId: `first-layer-${edge.asset}`,
        enforced: true,
      });
    }
  }
  // PASS 2b: every module tab the closure itself raised must clear its own CAP
  // (R1/R7) — e.g. R2 raising the Settings page pulls the Administration >
  // Solution Management master up to cover it. Iterated: a raised gate may
  // itself sit under a cap.
  for (let guard = 0; guard < MODULE_GATE_EDGES.length + 2; guard++) {
    const before = autoAssigned.length;
    for (const aa of [...autoAssigned]) {
      if (aa.target !== "utilityTab") continue;
      const capEdge = MODULE_GATE_EDGES.find(
        (e) => e.tabModule === aa.module && e.tab === aa.tab
      );
      if (!capEdge) continue;
      const need = rank(aa.to);
      if (moduleTabRank(u, capEdge.gateModule, capEdge.gateTab) < need) {
        raiseModuleTab(
          capEdge.gateModule,
          capEdge.gateTab,
          need,
          `module-gate-${capEdge.gateTab.toLowerCase().replace(/ /g, "-")}`
        );
      }
    }
    if (autoAssigned.length === before) break;
  }
  return { utils: u, servicePermissions: sp, autoAssigned };
}

// Lock on a BASE-PERMISSION cell: the highest requirement any currently selected source
// imposes on this module tab, with the requirer's name for the tooltip. { level, by }.
export function moduleTabLock(utils, servicePermissions, moduleName, tabName) {
  let level = 0;
  let by = "";
  for (const e of MODULE_MODULE_EDGES) {
    if (e.depModule !== moduleName || e.depTab !== tabName) continue;
    const src = moduleTabRank(utils, e.sourceModule, e.sourceTab);
    if (src < 1) continue;
    const want = e.depLevel === "same" ? src : rank(e.depLevel);
    if (want > level) {
      level = want;
      by = e.sourceLabel || e.sourceTab;
    }
  }
  for (const e of ASSET_MODULE_EDGES) {
    if (e.depModule !== moduleName || e.depTab !== tabName) continue;
    const triggered = (servicePermissions || []).some((row) =>
      (row.assets || []).some(
        (a) => a && a.name === e.asset && rank(a.permissions) >= e.minRank
      )
    );
    if (triggered && rank(e.depLevel) > level) {
      level = rank(e.depLevel);
      by = `solution permissions (${e.asset})`;
    }
  }
  // First-layer reverse lock: a Features/Administration/Parts master cannot drop below
  // the highest rank any held per-solution asset uses... is handled by cascade
  // (mirror-down with preview), NOT a lock — masters stay freely lowerable.
  return { level, by };
}

// Row-aware lock on a SOLUTION cell: module-source edges (Threshold Management chain)
// plus same-row asset edges (Digital Twin). { level, by } — by names the requirer.
// Attribution priority on a tie: a SCOPING source (Threshold Profiles) the user
// granted on this row is the most meaningful name, then any other row-local
// source (Rules / Digital Twin), then a module source (Threshold Management).
// Higher wins; a strictly greater LEVEL always wins regardless of priority.
const attrPriority = (by, isRowLocal) =>
  !isRowLocal ? 0 : isScopingAsset(by) ? 2 : 1;
export function cellDepLock(utils, row, asset, callerRank) {
  const info = lockedDepLevelInfo(utils, asset, callerRank);
  let level = info.level;
  let by = info.by;
  let prio = attrPriority(by, false); // module source
  for (const e of ASSET_ASSET_EDGES) {
    if (e.depAsset !== asset) continue;
    const src = ((row && row.assets) || []).find(
      (x) => x && x.name === e.sourceAsset
    );
    if (!src || rank(src.permissions) < 1) continue;
    const want = e.depLevel === "same" ? rank(src.permissions) : rank(e.depLevel);
    if (want < 1) continue;
    const p = attrPriority(e.sourceAsset, true);
    // A higher level always wins; on an equal level, the higher-priority source
    // name wins (so a granted Threshold Profiles beats a derived Rules, and any
    // row-local source beats an equal-level module source).
    if (want > level || (want === level && p > prio)) {
      level = want;
      by = e.sourceAsset;
      prio = p;
    }
  }
  return { level, by };
}

// Top-down gate rank for a solution asset: the current rank of its base-permission
// master (Infinity for ungated assets). Solution cell controls ABOVE this are greyed
// ("Requires <tab> under <module>") — the gate must be granted first.
export function gateRankFor(utils, asset) {
  const edge = ASSET_UTILITY_EDGES.find((e) => e.asset === asset);
  if (!edge) return Infinity;
  return moduleTabRank(utils, edge.module, edge.tab);
}
export function gateInfoFor(asset) {
  return ASSET_UTILITY_EDGES.find((e) => e.asset === asset) || null;
}

// Cap rank for a BASE-PERMISSION cell (R1/R7): the current rank of its gate tab,
// Infinity for uncapped tabs. Module cell controls ABOVE this are greyed
// ("Requires <gateTab> ...") — the gate must be raised first.
export function moduleTabGateRank(utils, moduleName, tabName) {
  const edge = MODULE_GATE_EDGES.find(
    (e) => e.tabModule === moduleName && e.tab === tabName
  );
  if (!edge) return Infinity;
  return moduleTabRank(utils, edge.gateModule, edge.gateTab);
}
export function moduleTabGateInfo(moduleName, tabName) {
  return (
    MODULE_GATE_EDGES.find((e) => e.tabModule === moduleName && e.tab === tabName) || null
  );
}

// Master-layer cascade (top-down model). Pure function of state, idempotent, run to a
// changed-flag fixpoint:
//   1. every gated solution cell is capped at its base-permission gate's CURRENT rank
//      (mirror-down: ALL -> READ when the gate drops to READ; cleared when removed);
//   2. every capped MODULE tab (R1/R7) is capped at its gate tab's current rank;
//   3. every mirror RAISE edge's source (R2 Creation, R3 Threshold Management) is
//      lowered to its lowest dep's rank (lowering a part cascades Creation down);
//      presence edges (SR-edge) deselect the source when the dep vanishes;
//   4. any enforced dependency source (Threshold Management) whose required asset no
//      longer holds on a held row is deselected (orphan removal).
// Returns { utils, servicePermissions, removed[] } — removed entries mirror the
// autoAssigned shape with from/to showing the lowering.
export function cascadeGates({ utils, servicePermissions }) {
  const u = JSON.parse(JSON.stringify(utils || []));
  const sp = JSON.parse(JSON.stringify(servicePermissions || []));
  const removed = [];
  const lowerModuleTab = (moduleName, tabName, toRank, edgeId) => {
    const m = u.find((x) => x && x.name === moduleName);
    const t = m && (m.tabs || []).find((x) => x && x.name === tabName);
    if (!t || rank(t.permission) <= toRank) return false;
    removed.push({
      target: "utilityTab",
      module: moduleName,
      tab: tabName,
      from: t.permission,
      to: permOf(toRank),
      edgeId,
    });
    t.permission = permOf(toRank);
    recomputeModuleOverall(m);
    return true;
  };
  const guard = ASSET_UTILITY_EDGES.length + MODULE_GATE_EDGES.length + MODULE_MODULE_EDGES.length + 4;
  for (let pass = 0; pass < guard; pass++) {
    let changed = false;
    // 1. Mirror gated solution cells down to the gate rank.
    for (const edge of ASSET_UTILITY_EDGES) {
      const gate = moduleTabRank(u, edge.module, edge.tab);
      for (const row of sp) {
        const a = (row.assets || []).find((x) => x && x.name === edge.asset);
        if (!a) continue;
        if (rank(a.permissions) > gate) {
          removed.push({
            target: "serviceAsset",
            serviceId: row.serviceId,
            serviceName: row.name,
            asset: edge.asset,
            from: a.permissions,
            to: permOf(gate),
            edgeId: `first-layer-${edge.asset}`,
          });
          a.permissions = permOf(gate);
          recomputeRowOverall(row);
          changed = true;
        }
      }
    }
    // 2. Mirror capped module tabs (R1/R7) down to their gate rank.
    for (const edge of MODULE_GATE_EDGES) {
      const gate = moduleTabRank(u, edge.gateModule, edge.gateTab);
      if (
        lowerModuleTab(
          edge.tabModule,
          edge.tab,
          Math.min(moduleTabRank(u, edge.tabModule, edge.tab), gate),
          `module-gate-${edge.gateTab.toLowerCase().replace(/ /g, "-")}`
        )
      )
        changed = true;
    }
    // 3. RAISE-edge sources follow their deps down (mirror: source <= dep;
    //    presence: source removed when the dep vanishes).
    for (const edge of MODULE_MODULE_EDGES) {
      const src = moduleTabRank(u, edge.sourceModule, edge.sourceTab);
      if (src < 1) continue;
      const dep = moduleTabRank(u, edge.depModule, edge.depTab);
      if (edge.depLevel === "same") {
        if (src > dep && lowerModuleTab(edge.sourceModule, edge.sourceTab, dep, edge.id))
          changed = true;
      } else if (dep < rank(edge.depLevel)) {
        if (lowerModuleTab(edge.sourceModule, edge.sourceTab, 0, edge.id)) changed = true;
      }
    }
    // 3b. Surface cap (item 1): a base "surface" tab (Analytics tabs) can never exceed its
    //     Solution Features feature. Lowering/dropping the feature caps/drops the surfaces.
    for (const edge of MODULE_SURFACE_EDGES) {
      const featRank = moduleTabRank(u, edge.featureModule, edge.featureTab);
      if (lowerModuleTab(edge.surfaceModule, edge.surfaceTab, featRank, edge.id))
        changed = true;
    }
    // 4. Deselect enforced sources whose dependency vanished from a held row.
    for (const edge of DEPENDENCY_EDGES) {
      if (!edge.enforced) continue;
      const m = u.find((x) => x && x.name === edge.sourceModule);
      const t = m && (m.tabs || []).find((x) => x && x.name === edge.sourceTab);
      if (!t || rank(t.permission) < 1) continue;
      // Presence semantic (mirrors the BE hasAnyTab net): the dep asset must be granted
      // (>= READ) on every row carrying ANY tab where its column exists; a row without the
      // column imposes nothing. Uses rowHasAnyTab so it stays symmetric with PASS 1's
      // enforced-edge net and cannot leave a save-blocking gap (audit finding 2).
      const orphaned = sp.some(
        (row) =>
          rowHasAnyTab(row) &&
          (row.assets || []).some(
            (a) => a && a.name === edge.depAsset && rank(a.permissions) < 1
          )
      );
      if (orphaned) {
        removed.push({
          target: "utilityTab",
          module: edge.sourceModule,
          tab: edge.sourceTab,
          from: t.permission,
          to: "disable",
          edgeId: edge.id,
        });
        t.permission = "disable";
        recomputeModuleOverall(m);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return { utils: u, servicePermissions: sp, removed };
}

// Difference between two editor states, as change entries (used for the impact preview).
export function diffPerms(before, after) {
  const out = [];
  const bu = before.utils || [], au = after.utils || [];
  for (const am of au) {
    if (!am || !am.name) continue;
    const bm = bu.find((x) => x && x.name === am.name);
    for (const at of am.tabs || []) {
      if (!at || !at.name) continue;
      const bt = bm && (bm.tabs || []).find((x) => x && x.name === at.name);
      const fromR = bt ? rank(bt.permission) : 0;
      const toR = rank(at.permission);
      if (fromR !== toR)
        out.push({ target: "utilityTab", module: am.name, tab: at.name, from: bt ? bt.permission : "disable", to: at.permission });
    }
  }
  const bs = before.servicePermissions || [], as_ = after.servicePermissions || [];
  for (const ar of as_) {
    if (!ar) continue;
    const br = bs.find((x) => x && String(x.serviceId) === String(ar.serviceId));
    for (const aa of ar.assets || []) {
      if (!aa || !aa.name) continue;
      const ba = br && (br.assets || []).find((x) => x && x.name === aa.name);
      const fromR = ba ? rank(ba.permissions) : 0;
      const toR = rank(aa.permissions);
      if (fromR !== toR)
        out.push({ target: "serviceAsset", serviceId: ar.serviceId, serviceName: ar.name, asset: aa.name, from: ba ? ba.permissions : "disable", to: aa.permissions });
    }
  }
  return out;
}

// Delegated-admin: block auto-assigns the caller cannot grant. heldServiceTabs =
// { [serviceId]: { [assetName]: rank } }, heldModuleTabs = { [moduleName]: { [tabName]: rank } },
// both from the caller's OWN (normalized) role. ⊤ bypasses.
export function findUngrantableAutoAssign(autoAssigned, heldServiceTabs, isUnscoped, heldModuleTabs) {
  if (isUnscoped) return null;
  for (const aa of autoAssigned || []) {
    if (!aa.enforced) continue; // advisory raises are already capped to caller
    if (aa.target === "utilityTab") {
      if (!heldModuleTabs) continue;
      const held = (heldModuleTabs[aa.module] && heldModuleTabs[aa.module][aa.tab]) || 0;
      if (held < rank(aa.to)) {
        return `Cannot grant this. It requires "${aa.tab}" (${permLabel(aa.to)}) under ${aa.module}, which you don't hold.`;
      }
      continue;
    }
    const held = (heldServiceTabs && heldServiceTabs[aa.serviceId] && heldServiceTabs[aa.serviceId][aa.asset]) || 0;
    if (held < rank(aa.to)) {
      return `Cannot grant "${aa.serviceName || aa.serviceId}". It requires "${aa.asset}" (${permLabel(aa.to)}) on that solution, which you don't hold.`;
    }
  }
  return null;
}
